import { ReadonlyVec2 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import { View } from '../../../view/View';
import { BrushCursorAction } from '../../actions/contouring-actions/BrushCursorAction';
import { DrawAction, DrawActionState } from '../../actions/DrawAction';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { MOUSE_BUTTON_LEFT } from '../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';

/**
 * {@link BrushTool} is a {@link Tool} that allows the user to draw SDFs.\
 * It is possible to introduce different types of brushes, by extending
 * this class and overriding the {@link BrushTool.resetCursorAction}
 * and {@link BrushTool.resetDrawAction} methods.
 */
export abstract class BrushTool extends Tool {
    public readonly toolType = 'brush';
    /**
     * Brush modifier keys
     */
    public static readonly BRUSH_SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    public static readonly BRUSH_ERASE_MODIFIER_KEYS = ModifierKeys.ALT;

    /**
     * Maximum diameter of the brush in millimeters.
     */
    public static readonly MAX_BRUSH_SIZE = 40;

    /**
     * Reference to the {@link RegionOfInterest} that is being drawn on.
     */
    protected readonly structure: RegionOfInterest;

    /**
     * Method to create a new {@link BrushTool} instance.
     */
    public constructor(
        erase: boolean,
        diameter: number,
        structure: RegionOfInterest,
        private readonly brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super();
        this._erase = erase;
        this._diameter = diameter;
        this.structure = structure;
    }

    /**
     * Method to inform the {@link BrushTool} about {@link onMouseWheel}-event.\
     * {@link BrushTool} checks if the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} are pressed and if so, it calls the {@link BrushTool.changeBrushSize}-method.\
     * If the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} are not pressed, the event is not handled.
     * @param delta Either 1 or -1, depending on the direction of the mouse wheel scroll.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse, in between [0, pixelSize - 1], at the time of the event.
     * @param view Optional {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED} if the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} were pressed and the {@link BrushTool.brushSize} was changed.\
     * Otherwise it returns {@link EventHandlerResultType.EVENT_NOT_HANDLED}.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(BrushTool.BRUSH_SIZE_MODIFIER_KEYS)) {
            let step: number;

            if (this.diameter <= 5) {
                step = 1;
            } else if (this.diameter <= 10) {
                step = 2;
            } else if (this.diameter <= 20) {
                step = 3;
            } else {
                step = 5;
            }
            this.diameter = Math.max(
                Math.min(
                    this._diameter + delta * step,
                    BrushTool.MAX_BRUSH_SIZE
                ),
                1.0
            );
            this._cursorAction?.updateBrushSize(this._diameter);
            this.brushSizeChangedCallback(this._diameter);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    protected _cursorAction?: BrushCursorAction;

    protected _drawAction: DrawAction | undefined;

    protected _drawState: DrawActionState | undefined;

    private _activeDrawingView: PlaneView | undefined;

    public override isActionInProgress(): boolean {
        return this._drawState !== undefined;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._activeDrawingView : undefined;
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModify === false) {
            this.resetDrawAction();
            enqueueSnackbar(
                'Modifying approved structures is not allowed. Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (
            button === MOUSE_BUTTON_LEFT &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION &&
            view.type === PlaneViewType.Primary
        ) {
            if (modifierKeys.equals(BrushTool.BRUSH_ERASE_MODIFIER_KEYS)) {
                this._erase = !this._erase;
            }
            view.snapToImagePixel();
            if (!this._drawAction) {
                this.resetDrawAction();
            }
            this._drawState = this._drawAction?.startAction(view, coordinates);
            this._activeDrawingView = view;
            this._cursorAction?.stopAction();
            if (modifierKeys.equals(BrushTool.BRUSH_ERASE_MODIFIER_KEYS)) {
                this._erase = !this._erase;
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        // Block other actions while drawing
        if (this.isActionInProgress()) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (this._drawAction) {
            const ret = this._drawAction.onMouseRelease(
                button,
                coordinates,
                view
            );
            this._drawAction = undefined;
            this._drawState = undefined;
            this._activeDrawingView = undefined;
            return ret;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        if (key === 'Escape' && this.isActionInProgress()) {
            const bb = this._drawState!.targetStructure.boundingBox;
            this._drawState!.clearMemory();
            this._drawState = undefined;
            this._drawAction = undefined;
            this._activeDrawingView = undefined;
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(bb, LayerBit.CONTOUR_LAYER_BIT, true)
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onDeactivate(): EventHandlerResult {
        if (this._drawAction) {
            const res = this._drawAction?.stopAction();
            this._drawAction = undefined;
            this._drawState = undefined;
            this._activeDrawingView = undefined;
            return res;
        }
        this._cursorAction?.stopAction();
        this._cursorAction = undefined;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    // recreate the draw action
    protected abstract resetDrawAction(): void;

    // recreate the cursor action
    protected abstract resetCursorAction(): void;

    /** Flag for erase mode */
    protected _erase: boolean = false;

    /**
     * Diameter of the brush in millimeters.\
     */
    protected _diameter: number = Number.NaN;

    /**
     * Method to change the size of the brush.\
     */
    public set diameter(diameter: number) {
        if (diameter > 1 && diameter < BrushTool.MAX_BRUSH_SIZE + 1e-4) {
            this._diameter = diameter;
        }
    }

    public get diameter(): number {
        return this._diameter;
    }
}
